import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, FormControlLabel, Radio, RadioGroup, Slider, Stack, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from '@mui/material';
import { api, type FloorPlanSolarResolution } from '../../services/api';
import { normalizeDegrees, solarPosition, solarTimeLabel } from '../siteplan/solarPosition';
import type { SolarSceneState } from '../solar/solarRig';
import SolarControls, { MONTHS, SolarSpinToggleButton, solarPanelSx } from '../solar/SolarControls';
import NorthCalibrationDialog from '../solar/NorthCalibrationDialog';
import { createFloorPlanScene, DEFAULT_WALL_HEIGHT_PERCENT, type FloorPlanWallsInfo, type FloorStyle } from './floorPlanScene';
import { DEFAULT_WALL_THRESHOLD, WALL_THRESHOLD_RANGE, type CropRect } from './floorPlanWalls';

export interface FloorPlanPreviewModel {
  northAngle: number | null; mirror: boolean; crop: CropRect | null; wallHeightPercent: number | null; wallThreshold?: number | null;
  latitude: number | null; longitude: number | null;
}

interface Props {
  unitId: number;
  imageUrl: string;
  /** e.g. "Blk 32 · #06-07 · Type C3F" */
  unitLabel: string;
  /** 3DS renders the sun rig; 3D is the same walls under fixed studio lighting. */
  sunlight: boolean;
  /** Admins only: shows Set / Calibrate north and lets the viewer write overrides. */
  canCalibrateNorth?: boolean;
  onFallback: () => void;
  /** Admin calibration preview: drives the model directly, no fetch, no control bar. */
  preview?: FloorPlanPreviewModel;
  compact?: boolean;
  /** Fires once walls are built (used by the admin dialog to seed the crop). */
  onWalls?: (info: FloorPlanWallsInfo) => void;
}

type Scope = 'stack' | 'block' | 'type';

const sourceLabel = (r: FloorPlanSolarResolution | null, canCalibrate: boolean) => {
  if (!r || r.northAngle == null) return canCalibrate ? 'North needs calibration' : 'Sunlight unavailable · north not calibrated for this floor plan yet';
  const angle = `${r.northAngle.toFixed(1)}°`;
  switch (r.source) {
    case 'stack': return `North calibrated · ${angle} · stack ${r.stack}`;
    case 'block': return `North calibrated · ${angle} · block ${r.block}`;
    case 'type': return `North calibrated · ${angle} · unit type${r.mirror ? ' · mirrored' : ''}`;
    case 'sitePlan': return `North estimated from site plan · ${angle} · confirm in Manage Floor Plan North`;
    default: return `North · ${angle}`;
  }
};

export default function SolarFloorPlan({ unitId, imageUrl, unitLabel, sunlight, canCalibrateNorth = false, onFallback, preview, compact, onWalls }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const engine = useRef<ReturnType<typeof createFloorPlanScene> | null>(null);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [month, setMonth] = useState(new Date().getMonth());
  const [minutes, setMinutes] = useState(930);
  const [playing, setPlaying] = useState(false);
  const [specialPlaying, setSpecialPlaying] = useState(false);
  const [rotating, setRotating] = useState(!reducedMotion && !preview);
  const [speed, setSpeed] = useState(1);
  const [compare, setCompare] = useState(false);
  const [enabled, setEnabled] = useState(Boolean(preview));
  const [resolution, setResolution] = useState<FloorPlanSolarResolution | null>(null);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState('');
  const [renderError, setRenderError] = useState('');
  const [walls, setWalls] = useState<FloorPlanWallsInfo | null>(null);
  // Calibration draft (admin, viewer-side).
  const [calibrating, setCalibrating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftNorth, setDraftNorth] = useState(0);
  const [draftMirror, setDraftMirror] = useState(false);
  const [draftCrop, setDraftCrop] = useState<CropRect | null>(null);
  const [draftWallHeight, setDraftWallHeight] = useState(DEFAULT_WALL_HEIGHT_PERCENT);
  const [draftThreshold, setDraftThreshold] = useState(DEFAULT_WALL_THRESHOLD);
  const [scope, setScope] = useState<Scope>('type');
  const [floorStyle, setFloorStyle] = useState<FloorStyle>('rendered');

  const latitude = preview ? preview.latitude : resolution?.latitude ?? null;
  const longitude = preview ? preview.longitude : resolution?.longitude ?? null;
  const validLocation = typeof latitude === 'number' && Number.isFinite(latitude) && Math.abs(latitude) <= 90
    && typeof longitude === 'number' && Number.isFinite(longitude) && Math.abs(longitude) <= 180;
  const north = preview ? preview.northAngle : calibrating ? draftNorth : resolution?.northAngle ?? null;
  const model = useMemo(() => ({ floorStyle, ...(preview
    ? { crop: preview.crop, mirror: preview.mirror, wallHeightPercent: preview.wallHeightPercent, wallThreshold: preview.wallThreshold ?? null }
    : calibrating
      ? { crop: scope === 'type' ? draftCrop : resolution?.crop ?? null, mirror: draftMirror,
        wallHeightPercent: scope === 'type' ? draftWallHeight : resolution?.wallHeight ?? null,
        wallThreshold: scope === 'type' ? draftThreshold : resolution?.wallThreshold ?? null }
      : { crop: resolution?.crop ?? null, mirror: resolution?.mirror ?? false, wallHeightPercent: resolution?.wallHeight ?? null,
        wallThreshold: resolution?.wallThreshold ?? null }) }),
  [preview, calibrating, scope, draftCrop, draftMirror, draftWallHeight, draftThreshold, resolution, floorStyle]);
  const sunOn = sunlight && enabled && north != null && validLocation;
  const sceneState = useRef<SolarSceneState>({} as SolarSceneState);
  sceneState.current = { month, minutes, north: north ?? 0, latitude: latitude ?? 0, longitude: longitude ?? 0,
    playing, specialPlaying, rotating, speed, enabled: sunOn, compare, reducedMotion };
  const pos = solarPosition(month, minutes, latitude ?? 0, longitude ?? 0);

  const load = useCallback(() => {
    if (preview) return () => {};
    let cancelled = false; setLoading(true); setError('');
    api.floorPlanSolar(unitId).then(result => {
      if (cancelled) return;
      setResolution(result);
      setEnabled(result.northAngle != null);
    }).catch(e => {
      if (cancelled) return;
      // 3D (fixed lighting) never needs the angle; a viewer without the 3DS
      // grant simply gets the auto-detected crop.
      if (sunlight) setError(e instanceof Error ? e.message : 'Unable to load north calibration');
      setResolution(null); setEnabled(false);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [preview, unitId, sunlight]);
  useEffect(() => load(), [load]);

  const wallsRef = useRef(onWalls); wallsRef.current = onWalls;
  const initialModel = useRef(model);
  useEffect(() => {
    if (!host.current) return;
    try {
      engine.current = createFloorPlanScene(host.current, { imageUrl, ...initialModel.current }, () => sceneState.current, setMinutes,
        () => setMonth(current => (current + 1) % MONTHS.length), setRenderError,
        info => { setWalls(info); wallsRef.current?.(info); });
    } catch { setRenderError('Your device could not start the 3D renderer. The 2D floor plan is still available.'); }
    return () => { engine.current?.dispose(); engine.current = null; };
  }, [imageUrl]);
  // Rebuilding walls re-analyses ~1.5 MP of pixels, so coalesce slider drags.
  useEffect(() => { const timer = setTimeout(() => engine.current?.updateModel(model), 180); return () => clearTimeout(timer); }, [model]);
  useEffect(() => { if (reducedMotion) { setPlaying(false); setSpecialPlaying(false); setRotating(false); } }, [reducedMotion]);

  const openCalibration = () => {
    const r = resolution;
    setDraftNorth(r?.northAngle ?? r?.sitePlanNorthAngle ?? 0);
    setDraftMirror(r?.mirror ?? false);
    setDraftCrop(r?.crop ?? walls?.suggestedCrop ?? walls?.crop ?? null);
    setDraftWallHeight(r?.wallHeight ?? DEFAULT_WALL_HEIGHT_PERCENT);
    setDraftThreshold(r?.wallThreshold ?? DEFAULT_WALL_THRESHOLD);
    setScope(r?.source === 'stack' ? 'stack' : r?.source === 'block' ? 'block' : r?.typeNorthAngle == null ? 'type' : 'stack');
    setError(''); setCalibrating(true);
  };
  const save = async () => {
    // Defence in depth: the PUT is admin-only server-side regardless.
    if (!canCalibrateNorth || !resolution?.unitTypeId || !resolution.imageUrl) { setCalibrating(false); return; }
    setSaving(true); setError('');
    try {
      await api.saveFloorPlanOrientation(resolution.projectId, {
        unitTypeId: resolution.unitTypeId, imageUrl: resolution.imageUrl,
        block: scope === 'type' ? '' : resolution.block, stack: scope === 'stack' ? resolution.stack : '',
        northAngle: normalizeDegrees(draftNorth), mirror: draftMirror,
        ...(scope === 'type' ? { crop: draftCrop, wallHeight: draftWallHeight, wallThreshold: draftThreshold } : {}),
      });
      setCalibrating(false); load();
    } catch (e) { setError(e instanceof Error ? e.message : 'Unable to save north'); }
    finally { setSaving(false); }
  };

  const height = compact ? { xs: 300, md: 360 } : { xs: 430, md: 'min(680px, 68vh)' };
  return <Box sx={{ bgcolor: '#101927', color: '#e8f0f8', borderRadius: 2, overflow: 'hidden' }}>
    <Box sx={{ position: 'relative', height, minHeight: compact ? 280 : 400 }}>
      <Box ref={host} sx={{ width: '100%', height: '100%', '& canvas': { display: 'block', touchAction: 'none' } }} />
      <Box sx={{ position: 'absolute', top: 16, left: 16, pointerEvents: 'none' }}>
        <Typography sx={{ color: '#e9b878', fontSize: 10, letterSpacing: '.22em', fontWeight: 700 }}>{sunlight ? 'SOLAR EXPLORER' : '3D FLOOR PLAN'}</Typography>
        {sunlight && !compact ? <>
          <Typography sx={{ fontSize: { xs: 25, md: 34 }, fontWeight: 300, fontVariantNumeric: 'tabular-nums' }}>{MONTHS[month]} · {solarTimeLabel(minutes)}</Typography>
          <Typography sx={{ fontSize: 11, color: '#adbed1' }}>15 {MONTHS[month]} 2026 · Singapore time</Typography>
        </> : null}
        <Typography sx={{ fontSize: compact ? 12 : { xs: 13, md: 15 }, fontWeight: 500, mt: 0.5 }}>{unitLabel}</Typography>
        {sunOn && <Typography sx={{ mt: 0.5, fontSize: 12, color: '#ffcc8a' }}>
          {pos.elevation > 0 ? `Sun ${Math.round(pos.azimuth)}° · ${Math.round(pos.elevation)}° above horizon` : 'Sun below horizon'}
        </Typography>}
        {walls && walls.spans === 0 && <Typography sx={{ mt: 0.5, fontSize: 11, color: '#f0a8a8' }}>No walls detected on this sheet · adjust the crop or wall detection</Typography>}
        {walls && walls.spans > 0 && !walls.footprintFound && floorStyle === 'rendered' && <Typography sx={{ mt: 0.5, fontSize: 11, color: '#f0c8a8' }}>Outline not closed · floor shown as a slab</Typography>}
      </Box>
      <Stack sx={{ position: 'absolute', top: 16, right: 12, ...solarPanelSx, px: 1 }}>
        <SolarSpinToggleButton rotating={rotating} onRotatingChange={setRotating} reducedMotion={reducedMotion} />
        <Button size="small" sx={{ color: '#d4e5f7' }} onClick={() => { setRotating(false); engine.current?.reset(); }}>Reset view</Button>
        <Button aria-label="Zoom in" sx={{ color: '#d4e5f7', minWidth: 36 }} onClick={() => engine.current?.zoom(0.85)}>＋</Button>
        <Button aria-label="Zoom out" sx={{ color: '#d4e5f7', minWidth: 36 }} onClick={() => engine.current?.zoom(1.15)}>−</Button>
        <ToggleButtonGroup size="small" exclusive value={floorStyle} onChange={(_, v) => v && setFloorStyle(v)} orientation="vertical" sx={{ mb: 0.5,
          '& .MuiToggleButton-root': { color: '#aebfd2', borderColor: 'rgba(184,211,240,.2)', fontSize: 10, py: 0.25, px: 1 },
          '& .Mui-selected': { color: '#172334 !important', bgcolor: '#ffca85 !important' } }}>
          <ToggleButton value="rendered">Rendered</ToggleButton>
          <ToggleButton value="drawing">Drawing</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {!compact && <Box sx={{ position: 'absolute', bottom: 12, left: 12, right: 12, display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'end' }}>
        <Typography sx={{ fontSize: 10, color: '#b5c4d5', ...solarPanelSx, p: 1 }}>Drag to orbit · Pinch to zoom<br />Illustrative walls, floor and shadows · no neighbouring blocks</Typography>
        {sunlight && compare && enabled && <Typography sx={{ fontSize: 10, ...solarPanelSx, p: 1 }}><span style={{ color: '#73cfff' }}>June</span> · <span style={{ color: '#b99aff' }}>December</span> · <span style={{ color: '#ffc66b' }}>Selected month</span></Typography>}
      </Box>}
      {renderError && <Alert severity="warning" sx={{ position: 'absolute', top: '45%', left: '10%', right: '10%' }} action={<Button onClick={onFallback}>2D view</Button>}>{renderError}</Alert>}
    </Box>
    {!preview && <SolarControls sunlight={sunlight} loading={loading} validLocation={validLocation} north={resolution?.northAngle ?? null} canCalibrateNorth={canCalibrateNorth}
      enabled={enabled} onEnabledChange={setEnabled} rotating={rotating} onRotatingChange={setRotating} reducedMotion={reducedMotion}
      month={month} onMonthChange={setMonth} minutes={minutes} onMinutesChange={setMinutes}
      playing={playing} onPlayingChange={setPlaying} specialPlaying={specialPlaying} onSpecialPlayingChange={setSpecialPlaying}
      speed={speed} onSpeedChange={setSpeed} compare={compare} onCompareChange={setCompare}
      onCalibrate={openCalibration} error={calibrating ? '' : error}
      statusText={sourceLabel(resolution, canCalibrateNorth)} massingText="Walls from the plan · fixed lighting" />}
    {sunlight && canCalibrateNorth && !preview && <NorthCalibrationDialog open={calibrating} imageUrl={imageUrl} value={draftNorth} onChange={setDraftNorth}
      title="Calibrate north for this floor plan"
      description="Rotate the arrow to match N on the sheet (or the key plan). Tick Mirror if this stack is the flipped version of the drawn plan. The dashed box should hug the drawn unit only; the 3D view behind updates live."
      mirror={draftMirror} onMirrorChange={setDraftMirror}
      crop={scope === 'type' ? draftCrop : undefined} onCropChange={scope === 'type' ? setDraftCrop : undefined}
      saving={saving} error={error} onCancel={() => setCalibrating(false)} onConfirm={save}>
      <Stack spacing={1} sx={{ mt: 2 }}>
        <Typography variant="subtitle2">Apply to</Typography>
        <RadioGroup row value={scope} onChange={(_, v) => setScope(v as Scope)}>
          <FormControlLabel value="stack" control={<Radio />} label={`This stack (Blk ${resolution?.block || '?'} · ${resolution?.stack || '?'})`} disabled={!resolution?.stack || !resolution?.block} />
          <FormControlLabel value="block" control={<Radio />} label={`Whole block ${resolution?.block || ''}`} disabled={!resolution?.block} />
          <FormControlLabel value="type" control={<Radio />} label={`Whole unit type${resolution?.unitType ? ` ${resolution.unitType}` : ''} (default)`} />
        </RadioGroup>
        {scope === 'type' && <Box sx={{ px: 1 }}>
          <Typography sx={{ fontSize: 12 }}>Wall height · {draftWallHeight.toFixed(1)}% of plan width</Typography>
          <Slider min={2} max={20} step={0.5} value={draftWallHeight} onChange={(_, v) => setDraftWallHeight(v as number)} valueLabelDisplay="auto" />
          <Typography sx={{ fontSize: 12 }}>Wall detection · {draftThreshold} {walls ? `· ${walls.spans} wall pieces found` : ''}</Typography>
          <Slider min={WALL_THRESHOLD_RANGE[0]} max={WALL_THRESHOLD_RANGE[1]} step={5} value={draftThreshold} onChange={(_, v) => setDraftThreshold(v as number)} valueLabelDisplay="auto" />
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Lower if shading is picked up as walls · higher if faint walls are missed</Typography>
        </Box>}
        {resolution?.source === 'sitePlan' && <Alert severity="info">Starting from the site plan north ({resolution.sitePlanNorthAngle?.toFixed(1)}°). Confirm or adjust it for this plan.</Alert>}
      </Stack>
    </NorthCalibrationDialog>}
  </Box>;
}
